import type {ChangeEvent, MouseEvent as ReactMouseEvent} from 'react';
import {useState} from 'react';

import {useThemeColors} from '@nfq/colors';
import {useFormField, type ValidationFunc} from '@nfq/react-form';

import {useOutsideClick} from '../../../hooks/useOutsideClick';
import {useSelectList} from '../hooks/useSelectList';

import type {Option} from '../../../../utils/childTypes';

/**
 * Custom hook for managing the state and behavior of a filter select input component.
 * This hook handles opening and closing the select dropdown, managing the selected value,
 * and triggering callbacks when the value changes. It also determines the position of the dropdown list
 * and whether the list should open above or below the select input.
 *
 * @param name          The name identifier for the form field, used for form validation and field tracking.
 * @param defaultValue  The default value to be selected when the component is first rendered.
 * @param hasSearch     Optional flag to enable search functionality within the dropdown, allowing users to filter options by typing.
 * @param options       The array of available options for the filter select. Each option includes a label and value.
 * @param onChange      Callback function to handle when the selected value changes.
 * @param isDisabled    A boolean flag that determines whether the filter select is disabled.
 * @param keepOnUnmount Optional flag to preserve form field value when component unmounts, defaults to false.
 * @param validation    Optional validation function to validate the selected value.
 * @returns An object containing state and functions to control the behavior of the filter select input.
 *
 * @example
 * ```tsx
 * const { choosedOption, handleChangeValue, handleOpenSelect, isOpen, selectRef } = useBaseDropdownInput(
 *   'initialValue',
 *   options,
 *   (newValue) => console.log(newValue),
 *   false
 * );
 * ```
 */
export const useBaseDropdownInput = (
    name: string,
    defaultValue: string,
    hasSearch: boolean,
    options: Option[],
    onChange: (value: any) => void,
    isDisabled: boolean,
    keepOnUnmount = false,
    validation: ValidationFunc = () => null
) => {
    /**
     * Gets the label corresponding to a given value from the options array.
     *
     * @param valueToSearchFor The value to get the label for.
     *
     * @returns The label corresponding to the given value, or an empty string if no matching option is found.
     */
    const getLabelForValue = (valueToSearchFor?: string) => options
        .filter(({value: optionValue}) => String(optionValue) === valueToSearchFor)[0]?.label ?? '';

    const colors = useThemeColors();
    const {error, handleFieldChange, value} = useFormField(name, defaultValue, defaultValue, validation, keepOnUnmount);
    const [isOpen, setIsOpen] = useState(false);
    const [searchValue, setSearchValue] = useState(getLabelForValue(defaultValue));
    const selectRef = useOutsideClick<HTMLDivElement>(
        setIsOpen,
        hasSearch ? () => setSearchValue(getLabelForValue(value)) : undefined
    );
    const {isTop, listHeight} = useSelectList(selectRef, isOpen);
    const filteredOptions = hasSearch
        ? options.filter(({label}) => label.toLowerCase().includes(searchValue.toLowerCase()))
        : options;

    const chosenOption = options.filter(
        ({value: optionValue}) => optionValue === value
    )[0]?.label ?? '';

    /**
     * Handles the action of opening the select dropdown.
     * If the select input is disabled, the function returns early and does not open the dropdown.
     * Otherwise, it sets the dropdown to be open by updating the `isOpen` state to `true`.
     * This function is used to trigger the display of the options list when the select input is clicked.
     */
    const handleToggleSelect = () => {
        if (isDisabled) return;

        if (!isOpen && hasSearch) {
            setSearchValue('');
        }

        setIsOpen(!isOpen);
    };

    /**
     * Event handler for handling change events on the search text input element.
     *
     * @param event The change event.
     */
    const handleChangeSearchText = (event: ChangeEvent<HTMLInputElement>) => {
        setSearchValue(event.target.value);
    };

    /**
     * Handles the action of selecting a new value from the dropdown options.
     * It stops the event propagation to prevent any parent click handlers from being triggered.
     * Then, it retrieves the selected value using the `index` stored in the dataset of the clicked option element.
     * Once the value is identified, it updates the state with the selected value, closes the dropdown, and triggers the `onChange` callback with the new value.
     *
     * @param event The mouse event triggered when an option is clicked.
     */
    const handleChangeValue = (event: ReactMouseEvent<HTMLDivElement>) => {
        const {index: fieldValue} = event.currentTarget.dataset;
        const realValue = options.filter(({value: optionValue}) => String(optionValue) === fieldValue)[0]?.value;

        setSearchValue(getLabelForValue(realValue));
        handleFieldChange(realValue);
        setIsOpen(false);
        onChange(realValue);
    };

    /**
     * Handles the action of clicking the delete button to clear the selected value.
     */
    const handleDeleteClicked = () => {
        setSearchValue('');
        handleFieldChange('');
        onChange('');
    };

    return {
        chosenOption,
        colors,
        error,
        filteredOptions,
        handleChangeSearchText,
        handleChangeValue,
        handleDeleteClicked,
        handleToggleSelect,
        isOpen,
        isTop,
        listHeight,
        searchValue,
        selectRef
    };
};